import { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CampaignListTable = ({ campaigns, onViewCampaign, onEditCampaign }) => {
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [statusFilter, setStatusFilter] = useState('all');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return 'ChevronsUpDown';
    return sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown';
  };

  const filteredCampaigns = campaigns?.filter(campaign => {
    if (statusFilter === 'all') return true;
    return campaign?.status?.toLowerCase() === statusFilter?.toLowerCase();
  });

  const sortedCampaigns = [...filteredCampaigns]?.sort((a, b) => {
    let aValue = a?.[sortField];
    let bValue = b?.[sortField];

    if (sortField === 'createdAt') {
      aValue = new Date(aValue)?.getTime();
      bValue = new Date(bValue)?.getTime();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'bg-success/10 text-success border-success/20';
      case 'scheduled':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'draft':
        return 'bg-muted text-muted-foreground border-border';
      case 'completed':
        return 'bg-success/10 text-success border-success/20';
      case 'paused':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'simulating':
        return 'bg-warning/10 text-warning border-warning/20';
      case 'failed':
        return 'bg-error/10 text-error border-error/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getRiskColor = (riskScore) => {
    if (riskScore <= 30) return 'text-success';
    if (riskScore <= 60) return 'text-warning';
    return 'text-error';
  };

  const getRiskLabel = (riskScore) => {
    if (riskScore <= 30) return 'Low';
    if (riskScore <= 60) return 'Medium';
    return 'High';
  };

  const getCanaryStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'passed':
        return 'bg-success/10 text-success border-success/20';
      case 'failed':
        return 'bg-error/10 text-error border-error/20';
      case 'not_run':
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getCanaryStatusLabel = (status) => {
    if (!status || status === 'not_run') return 'Not Run';
    return status?.charAt(0)?.toUpperCase() + status?.slice(1);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted border-b border-border">
            <tr>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                >
                  Campaign Name
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                >
                  Status
                  <Icon name={getSortIcon('status')} size={14} />
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Canary Status
                </span>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort('riskScore')}
                  className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                >
                  Risk Profile
                  <Icon name={getSortIcon('riskScore')} size={14} />
                </button>
              </th>
              <th className="px-6 py-4 text-left">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Performance
                </span>
              </th>
              <th className="px-6 py-4 text-left">
                <button
                  onClick={() => handleSort('createdAt')}
                  className="flex items-center gap-2 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
                >
                  Created
                  <Icon name={getSortIcon('createdAt')} size={14} />
                </button>
              </th>
              <th className="px-6 py-4 text-right">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedCampaigns?.map((campaign) => (
              <tr key={campaign?.id} className="hover:bg-muted/50 transition-colors">
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-foreground mb-1">
                      {campaign?.name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate max-w-xs">
                      {campaign?.subject}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                    getStatusColor(campaign?.status)
                  }`}>
                    {campaign?.status}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                    getCanaryStatusColor(campaign?.canaryStatus)
                  }`}>
                    {getCanaryStatusLabel(campaign?.canaryStatus)}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${
                      getRiskColor(campaign?.riskScore)
                    }`}>
                      {getRiskLabel(campaign?.riskScore)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      ({campaign?.riskScore}/100)
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="text-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-muted-foreground">Sent:</span>
                      <span className="font-medium text-foreground">
                        {campaign?.sent?.toLocaleString()}
                      </span>
                    </div>
                    {campaign?.delivered > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Delivered:</span>
                        <span className="font-medium text-success">
                          {((campaign?.delivered / campaign?.sent) * 100)?.toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-muted-foreground">
                    {formatDate(campaign?.createdAt)}
                  </span>
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => onViewCampaign?.(campaign)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      title="View Details"
                    >
                      <Icon name="Eye" size={16} className="text-muted-foreground" />
                    </button>
                    <button
                      onClick={() => onEditCampaign?.(campaign)}
                      className="p-2 hover:bg-muted rounded-lg transition-colors"
                      title="Edit Campaign"
                    >
                      <Icon name="Edit" size={16} className="text-muted-foreground" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CampaignListTable;